import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import api from './api';
import { SLOT_DEFAULTS } from './imagery';

/**
 * BrandImagesContext
 *
 * Loads admin-provided overrides for every image slot on the site
 * (see IMAGE_SLOTS in ./imagery.js) from GET /api/brand-images once at
 * app mount. Any consumer that needs an image should call:
 *
 *     const url = useBrandImage('mission.hero'); // returns override OR default
 *
 * Behaviour:
 *   • If the slot has an admin override → returns the override URL
 *   • Otherwise → returns the code default from SLOT_DEFAULTS
 *   • Unknown slot IDs → returns undefined (safe to pass a defaultUrl arg)
 */

const BrandImagesContext = createContext({
    overrides: {},
    isLoading: false,
    refresh: async () => {},
});

export function BrandImagesProvider({ children }) {
    const [overrides, setOverrides] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const res = await api.get('/brand-images');
            setOverrides(res?.data?.images || {});
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
                console.debug('[BrandImages] fetch failed, using defaults:', err?.message);
            }
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const value = useMemo(() => ({ overrides, isLoading, refresh }), [overrides, isLoading, refresh]);
    return <BrandImagesContext.Provider value={value}>{children}</BrandImagesContext.Provider>;
}

/**
 * useBrandImage(slotId, fallbackUrl?)
 * Returns override → SLOT_DEFAULTS[slotId] → fallbackUrl → undefined
 */
export function useBrandImage(slotId, fallbackUrl) {
    const { overrides } = useContext(BrandImagesContext);
    if (overrides && overrides[slotId]) return overrides[slotId];
    if (slotId && SLOT_DEFAULTS[slotId]) return SLOT_DEFAULTS[slotId];
    return fallbackUrl;
}

/**
 * useBrandImagesMap() — access the raw override map (used by admin page)
 */
export function useBrandImagesMap() {
    return useContext(BrandImagesContext);
}
